/**
 * Tela explicativa exibida ANTES do prompt de localização do sistema.
 *
 * Uso imperativo (funciona de qualquer hook/serviço):
 *   const ok = await askLocationPrimer();
 * O host `<LocationPermissionPrimerHost />` precisa estar montado uma vez
 * na árvore (ver DeferredAppHooks).
 */
import { useEffect, useState } from "react";
import { MapPin, ShieldCheck, EyeOff, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

type Resolver = (accepted: boolean) => void;

let openPrimer: ((resolve: Resolver) => void) | null = null;

/** Abre o primer. Resolve `true` se o usuário quiser continuar. */
export function askLocationPrimer(): Promise<boolean> {
  if (!openPrimer) return Promise.resolve(true); // host não montado: não bloqueia o fluxo
  return new Promise<boolean>((resolve) => openPrimer!(resolve));
}

export const LocationPermissionPrimerHost = () => {
  const [resolver, setResolver] = useState<{ fn: Resolver } | null>(null);

  useEffect(() => {
    openPrimer = (resolve) => setResolver({ fn: resolve });
    return () => {
      openPrimer = null;
    };
  }, []);

  const finish = (accepted: boolean) => {
    resolver?.fn(accepted);
    setResolver(null);
  };

  return (
    <Dialog open={!!resolver} onOpenChange={(o) => { if (!o) finish(false); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-2">
            <MapPin className="w-6 h-6 text-primary" aria-hidden="true" />
          </div>
          <DialogTitle className="font-display">Como usamos sua localização</DialogTitle>
          <DialogDescription className="font-body">
            O Zappy usa sua localização apenas enquanto o app está aberto, para mostrar os lugares
            perto de você e confirmar em qual estabelecimento você está.
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-3 text-sm font-body text-muted-foreground">
          <li className="flex gap-3">
            <ShieldCheck className="w-4 h-4 text-primary mt-0.5 shrink-0" aria-hidden="true" />
            <span>
              <strong className="text-foreground">O que nunca fazemos:</strong> não rastreamos você
              em segundo plano nem guardamos seu histórico de deslocamento.
            </span>
          </li>
          <li className="flex gap-3">
            <Clock className="w-4 h-4 text-primary mt-0.5 shrink-0" aria-hidden="true" />
            <span>
              Sua presença pode ser detectada, mas <strong className="text-foreground">você sempre
              confirma antes de aparecer</strong> no estabelecimento.
            </span>
          </li>
          <li className="flex gap-3">
            <EyeOff className="w-4 h-4 text-primary mt-0.5 shrink-0" aria-hidden="true" />
            <span>
              Nunca mostramos sua posição exata para outra pessoa. Os dados de localização são
              descartados ao fim do seu check-in.
            </span>
          </li>
        </ul>

        <div className="flex flex-col gap-2 pt-2">
          <Button onClick={() => finish(true)}>Entendi, continuar</Button>
          <Button variant="ghost" onClick={() => finish(false)}>
            Agora não
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LocationPermissionPrimerHost;
